import { FilterChip } from "@/components/FilterChip";
import { InlineError } from "@/components/QueryState";
import { BooMark } from "@/components/boo/BooMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AuditCategory, AuditEventView, AuditOutcome, AuditPage } from "@/lib/api";
import { CATEGORY_LABEL, OUTCOME_LABEL, VIA_LABEL, auditActorLabel, auditEventSummary, describeAuditEvent } from "@/lib/audit-copy";
import { AUDIT_RANGES, type AuditRange, type AuditSearch, RANGE_LABEL, hasAuditFilters } from "@/lib/audit-search";
import { cn } from "@/lib/utils";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import {
  Activity,
  Blocks,
  ChevronRight,
  CreditCard,
  KeyRound,
  LifeBuoy,
  Lock,
  LogIn,
  Plug,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";

// The audit log's filters and rows, shared by the org Audit log and a Boo's
// Activity tab. Filters live in the URL (lib/audit-search.ts).

const CATEGORY_ICON: Record<AuditCategory, LucideIcon> = {
  access: ShieldCheck,
  members: Users,
  sign_in: LogIn,
  sso: KeyRound,
  connectors: Plug,
  integrations: Blocks,
  secrets: Lock,
  spending: Wallet,
  org: Settings,
  billing: CreditCard,
  staff: LifeBuoy,
  boo_activity: Activity,
};

const OUTCOME_KEYS: AuditOutcome[] = ["success", "denied", "error"];

type Person = { user: { id: string; name: string; email: string } };

const CLEARED: Partial<AuditSearch> = { category: undefined, boo: undefined, person: undefined, outcome: undefined, range: undefined, from: undefined, to: undefined, action: undefined };

/** What, Boo (when `boos` is given), Person, When and Outcome; `end` sits at the right. */
export function AuditFilters({
  search,
  onChange,
  categories,
  boos,
  members,
  end,
}: {
  search: AuditSearch;
  onChange: (patch: Partial<AuditSearch>) => void;
  categories: AuditCategory[];
  boos?: Array<{ id: string; name: string }>;
  members: Person[];
  end?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterChip
        label="What"
        value={search.category ? CATEGORY_LABEL[search.category] : "Everything"}
        options={[{ key: "all", label: "Everything" }, ...categories.map((c) => ({ key: c, label: CATEGORY_LABEL[c] }))]}
        onSelect={(k) => onChange({ category: k === "all" ? undefined : (k as AuditCategory) })}
      />
      {boos && (
        <FilterChip
          label="Boo"
          value={search.boo ? (boos.find((b) => b.id === search.boo)?.name ?? "One Boo") : "All"}
          options={[{ key: "all", label: "All Boos" }, ...boos.map((b) => ({ key: b.id, label: b.name }))]}
          onSelect={(k) => onChange({ boo: k === "all" ? undefined : k })}
          contentClassName="max-h-80 overflow-y-auto"
        />
      )}
      <FilterChip
        label="Person"
        value={search.person ? (personLabel(members, search.person) ?? "One person") : "Anyone"}
        options={[{ key: "all", label: "Anyone" }, ...members.map((m) => ({ key: m.user.id, label: m.user.name || m.user.email }))]}
        onSelect={(k) => onChange({ person: k === "all" ? undefined : k })}
        contentClassName="max-h-80 overflow-y-auto"
      />
      <FilterChip
        label="When"
        value={RANGE_LABEL[search.range ?? "all"]}
        options={[{ key: "all", label: RANGE_LABEL.all }, ...AUDIT_RANGES.map((r) => ({ key: r, label: RANGE_LABEL[r] }))]}
        onSelect={(k) =>
          onChange(k === "custom" ? { range: "custom", from: search.from ?? daysAgo(30), to: search.to ?? daysAgo(0) } : { range: k === "all" ? undefined : (k as AuditRange), from: undefined, to: undefined })
        }
      />
      <FilterChip
        label="Outcome"
        value={search.outcome ? OUTCOME_LABEL[search.outcome] : "Any"}
        options={[{ key: "all", label: "Any" }, ...OUTCOME_KEYS.map((o) => ({ key: o, label: OUTCOME_LABEL[o] }))]}
        onSelect={(k) => onChange({ outcome: k === "all" ? undefined : (k as AuditOutcome) })}
      />
      {search.range === "custom" && <CustomRange from={search.from} to={search.to} onChange={onChange} />}
      {search.action && (
        <Button variant="secondary" size="sm" className="gap-1.5 font-normal" onClick={() => onChange({ action: undefined })} aria-label={`Stop showing only ${search.action}`}>
          <span className="text-muted-foreground">Event</span>
          <span className="font-mono text-xs">{search.action}</span>
          <X className="size-3.5 text-muted-foreground" />
        </Button>
      )}
      {hasAuditFilters(search) && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => onChange(CLEARED)}>
          Clear filters
        </Button>
      )}
      {end && <div className="ml-auto flex items-center gap-3">{end}</div>}
    </div>
  );
}

function personLabel(members: Person[], id: string): string | undefined {
  const m = members.find((x) => x.user.id === id);
  return m ? m.user.name || m.user.email : undefined;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function CustomRange({ from, to, onChange }: { from?: string; to?: string; onChange: (patch: Partial<AuditSearch>) => void }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Input type="date" value={from ?? ""} max={to} onChange={(e) => onChange({ from: e.target.value || undefined })} aria-label="From" className="h-8 w-36 text-xs tabular-nums" />
      <span aria-hidden>to</span>
      <Input type="date" value={to ?? ""} min={from} onChange={(e) => onChange({ to: e.target.value || undefined })} aria-label="To" className="h-8 w-36 text-xs tabular-nums" />
    </div>
  );
}

/** The events grouped by day, with Load more; `showBoo` adds the Boo column. */
export function AuditEventList({
  events,
  filtered,
  showBoo,
  emptyText,
}: {
  events: UseInfiniteQueryResult<InfiniteData<AuditPage, unknown>>;
  filtered: boolean;
  showBoo: boolean;
  emptyText: string;
}) {
  const rows = events.data?.pages.flatMap((p) => p.data) ?? [];
  if (events.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    );
  }
  if (events.isError) return <InlineError error={events.error} />;
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border px-4 py-12 text-center">
        <ScrollText className="size-5 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">{filtered ? "Nothing matches these filters." : emptyText}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      {/* One grid for every day, rows on subgrids: the Boo and outcome columns size to the widest cell on the page. */}
      <div className={cn("grid gap-x-5 gap-y-6", showBoo ? COLUMNS_WITH_BOO : COLUMNS)}>
        {groupByDay(rows).map(([day, items]) => (
          <div key={day} className="col-span-full grid grid-cols-subgrid gap-y-2">
            <h3 className="col-span-full text-xs font-medium uppercase tracking-wide text-muted-foreground">{day}</h3>
            <ol className="col-span-full grid grid-cols-subgrid divide-y divide-border overflow-hidden rounded-lg border border-border">
              {items.map((e) => (
                <AuditRow key={e.id} e={e} showBoo={showBoo} />
              ))}
            </ol>
          </div>
        ))}
      </div>
      {events.hasNextPage && (
        <Button variant="outline" size="sm" className="self-center" onClick={() => events.fetchNextPage()} loading={events.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </div>
  );
}

function groupByDay(rows: AuditEventView[]): Array<[string, AuditEventView[]]> {
  const out = new Map<string, AuditEventView[]>();
  for (const e of rows) {
    const day = new Date(e.createdAt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    out.set(day, [...(out.get(day) ?? []), e]);
  }
  return [...out.entries()];
}

// Columns: edge, time, sentence, [Boo,] outcome + chevron, edge. The zero-width
// edges plus the column gap make the rows' side padding, so hover fills the row.
const COLUMNS_WITH_BOO = "grid-cols-[0_4rem_minmax(0,1fr)_auto_auto_0]";
const COLUMNS = "grid-cols-[0_4rem_minmax(0,1fr)_auto_0]";

// Every cell's first box is one sentence line tall (h-6 = text-sm leading-6) and
// centers its content, so time, icon, Boo and outcome sit on the sentence's first line.
const LINE = "flex h-6 items-center";

const OUTCOME_TONE: Record<AuditOutcome, string> = {
  success: "border-border text-muted-foreground",
  denied: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
  error: "border-destructive/40 bg-destructive/10 text-destructive",
};

function OutcomeBadge({ outcome }: { outcome: AuditOutcome }) {
  return (
    <span className={cn("inline-flex h-5 items-center rounded-md border px-1.5 text-[11px] font-medium leading-none", OUTCOME_TONE[outcome] ?? OUTCOME_TONE.error)}>
      {OUTCOME_LABEL[outcome] ?? outcome}
    </span>
  );
}

function BooCell({ boo }: { boo: AuditEventView["boo"] }) {
  if (!boo) return <span className={cn(LINE, "text-xs text-muted-foreground/60")}>Organization</span>;
  const name = boo.name ?? "A deleted Boo";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(LINE, "min-w-0 max-w-44 gap-2")}>
          <BooMark id={boo.id} className="size-4 shrink-0" muted={!boo.name} />
          <span className="truncate text-xs text-muted-foreground">{name}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  );
}

function AuditRow({ e, showBoo }: { e: AuditEventView; showBoo: boolean }) {
  const [open, setOpen] = useState(false);
  const Icon = CATEGORY_ICON[e.category as AuditCategory] ?? ScrollText;
  const summary = auditEventSummary(e);
  const time = new Date(e.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const detailId = `audit-${e.id}`;
  return (
    <li className={cn("col-span-full grid grid-cols-subgrid", open && "bg-muted/30")}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
        className="col-span-full grid grid-cols-subgrid items-start py-3 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
      >
        <span className={cn(LINE, "col-start-2 text-xs tabular-nums text-muted-foreground")}>{time}</span>
        <span className="flex min-w-0 gap-2.5">
          <span className={cn(LINE, "shrink-0")}>
            <Icon className="size-4 text-muted-foreground" aria-hidden />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-sm leading-6 text-pretty [overflow-wrap:anywhere]">{describeAuditEvent(e)}</span>
            {summary && <span className="text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]">{summary}</span>}
            {e.reason && <span className="text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]">Reason: “{e.reason}”</span>}
          </span>
        </span>
        {showBoo && <BooCell boo={e.boo} />}
        <span className={cn(LINE, "justify-end gap-3")}>
          <OutcomeBadge outcome={e.outcome} />
          <ChevronRight className={cn("size-4 text-muted-foreground transition-transform duration-150", open && "rotate-90")} aria-hidden />
        </span>
      </button>
      {open && <AuditDetail id={detailId} e={e} />}
    </li>
  );
}

function AuditDetail({ id, e }: { id: string; e: AuditEventView }) {
  const who = auditActorLabel(e);
  const actorExtra = e.actor.type === "user" && e.actor.email && e.actor.email !== e.actor.label ? ` · ${e.actor.email}` : "";
  const fields: Array<[string, string | null]> = [
    ["When", new Date(e.createdAt).toLocaleString(undefined, { dateStyle: "full", timeStyle: "long" })],
    ["Who", `${who}${actorExtra}`],
    ["For", e.onBehalfOf ? e.onBehalfOf.name || e.onBehalfOf.email || e.onBehalfOf.userId : null],
    ["On", e.target ? e.target.label || e.target.id : null],
    ["Reason", e.reason],
    ["Through", e.via ? (VIA_LABEL[e.via] ?? e.via) : null],
    ["IP address", e.ip],
    ["Browser", e.userAgent],
    ["Event", e.action],
    ["Event ID", e.id],
  ];
  const metadata = e.metadata && Object.keys(e.metadata).length ? JSON.stringify(e.metadata, null, 2) : null;
  return (
    // Starts at the sentence text (sentence column + icon 1rem + gap 0.625rem) and ends with the outcome column.
    <div id={id} className="col-start-3 col-end-[-2] flex min-w-0 flex-col gap-3 pb-4 pl-[1.625rem] pt-1">
      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
        {fields
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className={cn("min-w-0 [overflow-wrap:anywhere]", (k === "Event" || k === "Event ID" || k === "IP address") && "font-mono")}>{v}</dd>
            </div>
          ))}
      </dl>
      {metadata && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Details</span>
          <pre className="max-h-64 overflow-auto rounded-md bg-muted/60 px-3 py-2 font-mono text-[11px] leading-relaxed">{metadata}</pre>
        </div>
      )}
    </div>
  );
}
